import React, { useState } from "react";
import {
  Button,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { clearResults } from "../../../../../features/OrgAdmin/orgAdminSlice";
import axios from "axios";
import md5 from "md5"; 
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

const EXTERNAL_INFO_MAP = {
  "Journeys Api": "getJourneys",
  "Geofence Api": "geofence",
  "ETA Api": "eta",
  "Attendance Api": "attendance",
  "Vendor Api Key": "vendorApiKey",
  "Migrate Journeys Api": "migrateJourneys",
  "Update Vehicle Api": "updateVehicle"
};

function ActionButtons() {
  const dispatch = useDispatch();
  const { formData, basicFormData, externalInfo, selectedOrg } = useSelector(
    (state) => state.org
  );
  const token = localStorage.getItem("token");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const isUpdateMode = !!selectedOrg;

  const handleClear = () => {
    dispatch(clearResults());
  };

  const handleDeleteClick = () => {
    if (!selectedOrg?._id) {
      setSnackbar({
        open: true,
        message: "Please select an organization first!",
        severity: "warning",
      });
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOrg = async () => {
    if (deletePassword !== "12345") {
      setSnackbar({
        open: true,
        message: "Incorrect Password! Deletion failed.",
        severity: "error",
      });
      return;
    }

    try {
      const orgHandle = selectedOrg.orgInfo.orgHandle;
      const url = `https://v3.dhundhoo.com/admin/org/${orgHandle}`;

      await axios.delete(url);

      setSnackbar({
        open: true,
        message: "Organization deleted successfully!",
        severity: "success",
      });
      
      setDeleteDialogOpen(false);
      setDeletePassword("");
      handleClear();
    } catch (error) {
      console.error("Error deleting org:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete organization!",
        severity: "error",
      });
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletePassword("");
  };

  const downLoadVehiclList = async () => {
    if (!selectedOrg?._id) {
      setSnackbar({
        open: true,
        message: "Please select an organization first!",
        severity: "warning",
      });
      return;
    }

    try {
      const response = await axios.get(
        `https://v3.dhundhoo.com/superAdmin/vendor/thing/vehicledevicedetails/${selectedOrg._id}`,
        {
          headers: { "x-dhundhoo-session": token },
        }
      );

      const jsonData = response.data;
      const dataArray = Object.entries(jsonData).map(([vehicle, imei]) => ({
        VehicleNumber: vehicle,
        IMEI: imei,
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataArray);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicles");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const fileName = `${selectedOrg.orgInfo.orgName}_vehicles_lists.xlsx`;

      FileSaver.saveAs(blob, fileName);

      setSnackbar({
        open: true,
        message: "Vehicle list downloaded successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to download vehicle list!",
        severity: "error",
      });
    }
  };

  const downLoadVehiclLinks = async () => {
    if (!selectedOrg?._id) {
      setSnackbar({
        open: true,
        message: "Please select an organization first!",
        severity: "warning",
      });
      return;
    }

    try {
      const response = await axios.get(
        `https://v3.dhundhoo.com/superAdmin/vendor/thing/links/${selectedOrg._id}`,
        {
          headers: { "x-dhundhoo-session": token },
        }
      );

      const jsonData = response.data;
      const dataArray = Object.entries(jsonData).map(([vehicle, link]) => ({
        VehicleNumber: vehicle,
        WebViewLink: link,
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataArray);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicles");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const fileName = `${selectedOrg.orgInfo.orgName}_WebViewLinks.xlsx`;

      FileSaver.saveAs(blob, fileName);

      setSnackbar({
        open: true,
        message: "Vehicle link downloaded successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to download vehicle link!",
        severity: "error",
      });
    }
  };

  const handleCreateOrg = async () => {
    const formattedExternalInfo = externalInfo.reduce((acc, item) => {
      if (item.key && item.value) {
        const apiKey = EXTERNAL_INFO_MAP[item.key] || item.key;
        acc[apiKey] = item.value;
      }
      return acc;
    }, {});

    const encodedPassword = basicFormData.password
      ? md5(basicFormData.password)
      : "";

    const payload = {
      orgName: basicFormData.orgName,
      address: basicFormData.orgAddress,
      apiKey: formData.webApiKey,
      avatar: basicFormData.avatar,
      config: {
        wallet: formData.paymentWallet === "enable" ? "ENABLED" : "DISABLED",
        immobilization: "ENABLED",
        push_option:
          formData.smsFeature === "pushOnly"
            ? "PUSH_ONLY"
            : formData.smsFeature === "optimized"
              ? "OPTIMIZED"
              : formData.smsFeature === "sms"
                ? "SMS"
                : "DISABLED",
        sms_option:
          formData.dhundhooLink === "dontSend"
            ? "SMS_NO_LINK"
            : "SMS_WITH_LINK",
        attendance_option:
          formData.attendance === "enable" ? "ENABLED" : "DISABLED",
        odometer: formData.odometer === "enable" ? "ENABLED" : "DISABLED",
        logs_option: formData.logs === "enable" ? "ENABLED" : "DISABLED",
        management: formData.management === "enable" ? "ENABLED" : "DISABLED",
        routeOptimization:
          formData.routeOptimization === "enable" ? "ENABLED" : "DISABLED",
        alert: formData.alerts === "enable" ? "ENABLED" : "DISABLED",
        planning: formData.planning === "enable" ? "ENABLED" : "DISABLED",
        trip: formData.tripDetails === "enable" ? "ENABLED" : "DISABLED",
        jobRole: formData.jobRole === "enable" ? "ENABLED" : "DISABLED",
        fuelLog: formData.fuelLog === "enable" ? "ENABLED" : "DISABLED",
        liveCamera: formData.liveCamera === "enable" ? "ENABLED" : "DISABLED",
        alertEmailIds: basicFormData.alertEmailIds.join(",") || "",
        vendor_option:
          formData.vendorConfigurations === "vendorOnly"
            ? "VENDOR_ONLY"
            : formData.vendorConfigurations === "dhundhooVendor"
              ? "DHUNDHOO_VENDOR"
              : formData.vendorConfigurations === "vendorPlus"
                ? "ENABLE_VENDOR_PLUS"
                : "NO_VENDOR",
      },
      disableJourneys: formData.journeyStatus === "disabled",
      emailId: basicFormData.emailId,
      externalInfo: formattedExternalInfo,
      orgCode: basicFormData.orgShortCode,
      orgHandle: isUpdateMode ? selectedOrg.orgInfo.orgHandle : "",
      phoneNumber: basicFormData.phoneNumber,
      speedLimit: formData.speedLimit / 3.6,
      licenseCount: formData.licenseCount,
      type: "ORG_SCHOOL",
      vendorHandle: formData.vendorHandle,
      encodedPassword,
    };

    if (!isUpdateMode) {
      payload.signUp = {
        firstName: basicFormData.firstName || "",
        lastName: basicFormData.lastName || "",
        phoneNumber: basicFormData.phoneNumber ? `+91${basicFormData.phoneNumber}` : "",
        emailId: basicFormData.emailId,
        encodedPassword,
        installToken: crypto.randomUUID(),
        mode: "EMAIL",
        portal: basicFormData.portal || "null",
      };
    }

    try {
      const url = "https://v3.dhundhoo.com/auth/upsertOrg";
      const response = await axios.post(url, payload, {
        headers: { "x-dhundhoo-session": token },
      });

      setSnackbar({
        open: true,
        message: isUpdateMode
          ? "Organization updated successfully!"
          : "Organization created successfully!",
        severity: "success",
      });
      handleClear();
    } catch (error) {
      setSnackbar({
        open: true,
        message: isUpdateMode
          ? "Failed to update organization!"
          : "Failed to create organization!",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          mt: 3,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={handleCreateOrg}
          disabled={!basicFormData.orgName || !basicFormData.emailId}
          sx={{ minWidth: 140, color: "white" }}
        >
          {isUpdateMode ? "Update Org" : "Create Org"}
        </Button>

        <Button
          variant="outlined"
          onClick={handleClear}
          sx={{ minWidth: 120 }}
          style={{ color: "black" }}
        >
          Clear
        </Button>

        {isUpdateMode && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteClick}
            sx={{ minWidth: 140, color: "#FFF",backgroundColor: "#d32f2f" }}
          >
            Delete Org
          </Button>
        )}

        {isUpdateMode && (
          <Button
            variant="contained"
            color="info"
            onClick={downLoadVehiclLinks}
            sx={{ minWidth: 140, color: "white" }}
          >
            Download Links
          </Button>
        )}

        {isUpdateMode && (
          <Button
            variant="contained"
            color="info"
            onClick={downLoadVehiclList}
            sx={{ minWidth: 140, color: "white" }}
          >
            Vehicle List
          </Button>
        )}

      </Box>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete Organization</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Enter Password to Confirm"
            type="password"
            fullWidth
            variant="outlined"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteOrg} color="error" variant="contained">
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ActionButtons;